import React, { useEffect, useMemo, useState } from 'react';
import BillingPicker from './BillingPicker';
import { withStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { navigate } from '@reach/router';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { parseBillingTotals } from './billingMetricsUtils';
import {
  resolveBillingTimeZone,
  getCurrentMonthKey,
  buildMonthPickerOptions,
  getMonthDateRange,
  formatMonthRangeLabel,
} from './billingDateUtils';
import { formatLocationLabel } from './billingDirectoryUtils';

const styles = (theme) => ({
  container: {
    width: '100%',
    padding: theme.spacing(2),
    // Let the billing view size naturally within the dashboard frame
    height: '150vh',
    // (no forced viewport height that can push content outside)
  },
  topRow: {
    marginBottom: theme.spacing(3),
  },
  metricCard: {
    height: '100%',
  },
  metricLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  metricValue: {
    fontSize: 24,
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  chartCard: {
    marginTop: theme.spacing(1),
    overflow: 'visible', // allow dropdowns to extend outside the card
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(2),
  },
});

// Match the dashboard Twilio color palette
const TWILIO_CHART_COLORS = [
  '#1b73e8',
  '#34a853',
  '#fbbc05',
  '#ea4335',
  '#a142f4',
  '#00acc1',
  '#fb8c00',
  '#0d9488',
  '#ef4444',
  '#6366f1',
  '#14b8a6',
  '#ff6d00',
];

const MessagingBilling = ({ classes }) => {
  const { t } = useTranslation();
  const timezone = useMemo(() => resolveBillingTimeZone(), []);
  const monthOptions = useMemo(
    () => buildMonthPickerOptions({ timezone }),
    [timezone]
  );
  const [selectedMonth, setSelectedMonth] = useState(() =>
    getCurrentMonthKey(timezone)
  );
  const monthRange = useMemo(
    () => getMonthDateRange(selectedMonth, timezone),
    [selectedMonth, timezone]
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountOptions, setAccountOptions] = useState([
    { value: 'all', label: 'All accounts' },
  ]);
  const [locationOptions, setLocationOptions] = useState([
    { value: 'all', label: 'All locations' },
  ]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [activeMonthIndex, setActiveMonthIndex] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);

  const serviceTotals = useMemo(
    () => parseBillingTotals((usageSummary && usageSummary.totals) || {}),
    [usageSummary]
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchMonthlySpend() {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          startDate: monthRange.startDate,
          endDate: monthRange.endDate,
          timezone,
        });
        if (selectedAccount !== 'all') {
          params.append('accountIds', selectedAccount);
        }
        if (selectedLocation !== 'all') {
          params.append('boxIds', selectedLocation);
        }
        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}dashboard/twilio/monthly-spend?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization:
                'Bearer ' +
                JSON.parse(sessionStorage.getItem('user')).signInUserSession.idToken
                  .jwtToken,
            },
          }
        );
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const body = await res.json();
        if (cancelled) return;
        const next = body && body.range ? body : null;
        setData(next);
        if (next && next.uiAccounts && next.uiAccounts.length) {
          const accounts = buildAccountOptions(next.uiAccounts);
          setAccountOptions(accounts);
          if (!accounts.some((opt) => opt.value === selectedAccount)) {
            setSelectedAccount('all');
          }
        }
        if (next) {
          const locations = buildLocationOptions(next, selectedAccount);
          setLocationOptions(locations);
          if (!locations.some((opt) => opt.value === selectedLocation)) {
            setSelectedLocation('all');
          }
        }
        if (!next || !next.range) {
          setError('Unable to load monthly spend data.');
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Unable to load monthly spend data.');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchMonthlySpend();
    return () => {
      cancelled = true;
    };
  }, [monthRange.startDate, monthRange.endDate, timezone, selectedAccount, selectedLocation]);

  useEffect(() => {
    let cancelled = false;
    async function fetchUsageSummary() {
      try {
        const params = new URLSearchParams({
          startDate: monthRange.startDate,
          endDate: monthRange.endDate,
          timezone,
        });
        if (selectedAccount !== 'all') {
          params.append('accountIds', selectedAccount);
        }
        const res = await fetch(
          `${process.env.REACT_APP_BASEURL}dashboard/twilio/usage-metrics?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization:
                'Bearer ' +
                JSON.parse(sessionStorage.getItem('user')).signInUserSession
                  .idToken.jwtToken,
            },
          }
        );
        if (!res.ok) {
          return;
        }
        const body = await res.json();
        if (!cancelled) {
          setUsageSummary(body || null);
          if (body && body.uiAccounts && body.uiAccounts.length) {
            const accounts = buildAccountOptions(body.uiAccounts);
            setAccountOptions(accounts);
          }
          if (body) {
            const locations = buildLocationOptions(body, selectedAccount);
            setLocationOptions(locations);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
    fetchUsageSummary();
    return () => {
      cancelled = true;
    };
  }, [monthRange.startDate, monthRange.endDate, timezone, selectedAccount]);

  useEffect(() => {
    if (!data) {
      setLocationOptions([{ value: 'all', label: 'All locations' }]);
      setSelectedLocation('all');
      return;
    }
    const locations = buildLocationOptions(data, selectedAccount);
    setLocationOptions(locations);
    if (!locations.some((opt) => opt.value === selectedLocation)) {
      setSelectedLocation('all');
    }
  }, [data, selectedAccount, selectedLocation]);

  const chartPayload = useMemo(() => {
    if (!data) {
      return { categories: [], series: [] };
    }
    // Try to build location series first if locations exist
    const locations = ensureArray(data && data.locations);
    if (locations.length) {
      const locationSeries = buildLocationSeries(
        data,
        selectedAccount,
        selectedLocation
      );
      if (locationSeries.series.length) {
        return locationSeries;
      }
    }
    // Fall back to combined series
    const combinedMeta = buildCombinedCategories(data);
    return {
      categories: combinedMeta.map((entry) => entry.label),
      series: buildCombinedSeries(data, combinedMeta),
    };
  }, [data, selectedAccount, selectedLocation]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        background: 'transparent',
        toolbar: { show: true },
        animations: { enabled: true },
        events: {
          dataPointSelection: (event, chartContext, { dataPointIndex }) => {
            setActiveMonthIndex(dataPointIndex);
          },
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '45%',
          borderRadius: 6,
        },
      },
      colors: TWILIO_CHART_COLORS,
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff'],
      },
      xaxis: {
        categories: chartPayload.categories,
        labels: { rotate: -35, trim: false },
      },
      yaxis: {
        title: { text: 'Amount' },
        labels: {
          formatter: (value) =>
            formatCurrency(value, (data && data.currency) || 'USD'),
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
      tooltip: {
        // Combined tooltip with required per-location data for the hovered month
        shared: true,
        intersect: false,
        custom: ({ dataPointIndex, w }) =>
          buildTooltipAll(
            w && w.config && w.config.series,
            chartPayload.categories,
            (data && data.currency) || 'USD',
            dataPointIndex
          ),
      },
    }),
    [chartPayload, data && data.currency]
  );

  return (
    <div className={classes.container}>
      <Typography variant="h5" gutterBottom>
        Billing overview
      </Typography>

      <Card className={classes.chartCard} style={{ overflow: 'visible' }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Historical monthly spend
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Historical monthly spend includes the total for billed and all linked accounts.
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => navigate('/home/billing/invoices')}
            >
              View Invoices
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => navigate('/home/billing-analytics')}
            >
              {t('Billing.Analytics.ViewDetailed')}
            </Button>
          </div>
          <Typography variant="caption" color="textSecondary" gutterBottom>
            {t('Billing.Historical.ClickToExpand')}
          </Typography>
          {usageSummary && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 12,
                marginBottom: 16,
              }}
            >
              {[
                { label: 'SMS', value: formatCurrency(serviceTotals.sms.spend, data && data.currency) },
                { label: 'MMS', value: formatCurrency(serviceTotals.mms.spend, data && data.currency) },
                { label: 'Voice', value: formatCurrency(serviceTotals.voice.spend, data && data.currency) },
                { label: 'Video', value: formatCurrency(serviceTotals.video.spend, data && data.currency) },
                {
                  label: 'Carrier & A2P',
                  value: formatCurrency(
                    serviceTotals.carrierCharges + serviceTotals.a2pRegistrationCharges,
                    data && data.currency
                  ),
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                    padding: '10px 12px',
                    background: '#f8fafc',
                  }}
                >
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
                    {item.label}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className={classes.filterRow}>
            <BillingPicker
              label="Month"
              value={selectedMonth}
              options={monthOptions}
              onChange={setSelectedMonth}
            />
            <Typography variant="caption" color="textSecondary" style={{ alignSelf: 'center' }}>
              {formatMonthRangeLabel(selectedMonth, timezone)}
            </Typography>
            <BillingPicker
              label="Account"
              value={selectedAccount}
              options={accountOptions}
              onChange={setSelectedAccount}
            />
            <BillingPicker
              label="Location"
              value={selectedLocation}
              options={locationOptions}
              onChange={setSelectedLocation}
            />
          </div>
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : loading ? (
            <Typography variant="body2" color="textSecondary">
              Loading monthly spend…
            </Typography>
          ) : chartPayload.series.length && chartPayload.categories.length ? (
            <React.Fragment>
              <ReactApexChart
                options={chartOptions}
                series={chartPayload.series}
                type="bar"
                height={360}
              />
              <LocationDetailsPanel
                activeMonthIndex={activeMonthIndex}
                categories={chartPayload.categories}
                series={chartPayload.series}
                currency={(data && data.currency) || 'USD'}
                onClose={() => setActiveMonthIndex(null)}
              />
            </React.Fragment>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No monthly spend data for this selection.
            </Typography>
          )}
          {data && data.range && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: 'block', marginTop: 8, textAlign: 'right' }}
            >
              {formatFootnote(
                data.range,
                (data && data.currency) || 'USD',
                chartPayload.categories.length
              )}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const LocationDetailsPanel = ({
  activeMonthIndex,
  categories,
  series,
  currency,
  onClose,
}) => {
  if (
    activeMonthIndex == null ||
    !categories ||
    !categories.length ||
    !Array.isArray(series) ||
    !series.length
  ) {
    return null;
  }

  const label = categories[activeMonthIndex] || '';

  const rows = series
    .map((entry, seriesIndex) => {
      const point =
        entry &&
        Array.isArray(entry.data) &&
        entry.data[activeMonthIndex];
      if (!point || Number(point.y) === 0) {
        return null;
      }
      const totals = parseBillingTotals((point.meta && point.meta.totals) || {});
      const sms = totals.sms || {};
      const mms = totals.mms || {};
      const voice = totals.voice || {};
      const video = totals.video || {};
      return {
        key: `${seriesIndex}-${activeMonthIndex}`,
        color: TWILIO_CHART_COLORS[seriesIndex % TWILIO_CHART_COLORS.length],
        name:
          (point.meta && point.meta.locationName) ||
          entry.name ||
          'Location',
        totalSpend: totals.totalSpend || point.y || 0,
        smsSpend: sms.spend || 0,
        mmsSpend: mms.spend || 0,
        voiceSpend: voice.spend || 0,
        videoSpend: video.spend || 0,
        smsOut: (sms.outbound && sms.outbound.count) || 0,
        smsOutSeg: (sms.outbound && sms.outbound.segments) || 0,
        smsIn: (sms.inbound && sms.inbound.count) || 0,
        smsInSeg: (sms.inbound && sms.inbound.segments) || 0,
        mmsOut: (mms.outbound && mms.outbound.count) || 0,
        mmsOutSeg: (mms.outbound && mms.outbound.segments) || 0,
        mmsIn: (mms.inbound && mms.inbound.count) || 0,
        mmsInSeg: (mms.inbound && mms.inbound.segments) || 0,
        voiceMin: voice.minutes || 0,
        videoMin: video.participantMinutes || 0,
      };
    })
    .filter(Boolean);

  if (!rows.length) {
    return null;
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.4)',
          background:
            'linear-gradient(90deg, rgba(37,99,235,0.95), rgba(56,189,248,0.95))',
          color: '#ffffff',
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 600 }}>{label}</span>
        <span
          style={{
            fontSize: 11,
            color: 'rgba(241, 245, 249, 0.9)',
            marginLeft: 8,
          }}
        >
          Location breakdown
        </span>
        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              marginLeft: 'auto',
              color: '#fecaca', // light red
              '&:hover': {
                color: '#fee2e2',
                backgroundColor: 'rgba(248, 113, 113, 0.25)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        dividers
        style={{
          maxHeight: 420,
          background: 'linear-gradient(to bottom, #f9fafb, #ffffff)',
        }}
      >
        <div
          style={{
            maxHeight: 380,
            overflowY: 'auto',
          }}
        >
          {rows.map((row) => (
            <div
              key={row.key}
              style={{
                marginBottom: 10,
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid rgba(209, 213, 219, 0.8)',
                background: '#ffffff',
                padding: '8px 10px',
                boxShadow:
                  '0 1px 1px rgba(15, 23, 42, 0.02), 0 1px 3px rgba(15, 23, 42, 0.08)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  marginBottom: 2,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: row.color,
                    marginRight: 6,
                  }}
                />
                <span>{row.name}</span>
                <span style={{ marginLeft: 'auto' }}>
                  {formatCurrency(row.totalSpend, currency)}
                </span>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#6b7280',
                  lineHeight: 1.5,
                  paddingLeft: 16,
                  marginTop: 4,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 2 }}>SMS</div>
                <div style={{ paddingLeft: 8 }}>
                  <div>Spend: {formatCurrency(row.smsSpend, currency)}</div>
                  <div>Inbound: {row.smsInSeg} segments</div>
                  <div>Outbound: {row.smsOutSeg} segments</div>
                </div>
                <div style={{ fontWeight: 600, marginTop: 6, marginBottom: 2 }}>MMS</div>
                <div style={{ paddingLeft: 8 }}>
                  <div>Spend: {formatCurrency(row.mmsSpend, currency)}</div>
                  <div>Inbound: {row.mmsInSeg} segments</div>
                  <div>Outbound: {row.mmsOutSeg} segments</div>
                </div>
                <div style={{ fontWeight: 600, marginTop: 6, marginBottom: 2 }}>Voice</div>
                <div style={{ paddingLeft: 8 }}>
                  <div>Spend: {formatCurrency(row.voiceSpend, currency)}</div>
                  <div>Minutes: {row.voiceMin}</div>
                </div>
                <div style={{ fontWeight: 600, marginTop: 6, marginBottom: 2 }}>Video</div>
                <div style={{ paddingLeft: 8 }}>
                  <div>Spend: {formatCurrency(row.videoSpend, currency)}</div>
                  <div>Participant min: {row.videoMin}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function buildLocationSeries(payload, accountId, locationId) {
  const locations = ensureArray(payload && payload.locations);
  if (!locations.length) {
    return { categories: [], series: [] };
  }
  
  // Filter locations by account and location if specified
  let filteredLocations = locations;
  if (accountId && accountId !== 'all') {
    filteredLocations = filteredLocations.filter(
      (loc) => String(loc && loc.accountId) === String(accountId)
    );
  }
  if (locationId && locationId !== 'all') {
    filteredLocations = filteredLocations.filter(
      (loc) => {
        const locId = loc && (loc.boxId || loc.id);
        return String(locId) === String(locationId);
      }
    );
  }
  
  if (!filteredLocations.length) {
    return { categories: [], series: [] };
  }
  
  // Collect all months across all locations
  const monthMeta = new Map();
  filteredLocations.forEach((location) => {
    ensureArray(location && location.months).forEach((entry) => {
      if (!entry) {
        return;
      }
      const key = entry.month || entry.label;
      if (!key) {
        return;
      }
      if (!monthMeta.has(key)) {
        monthMeta.set(key, {
          key: String(key),
          label: formatMonth(entry.month || key, entry.label),
        });
      }
    });
  });
  const months = Array.from(monthMeta.values()).sort((a, b) =>
    (a.key || '').localeCompare(b.key || '')
  );
  const categories = months.map((meta) => meta.label);

  const series = filteredLocations
    .map((location) => {
      const monthLookup = new Map();
      ensureArray(location && location.months).forEach((entry) => {
        if (!entry) {
          return;
        }
        const key = entry.month || entry.label;
        if (key) {
          monthLookup.set(String(key), entry);
        }
      });
      const name =
        (location && location.boxName) ||
        (location && location.name) ||
        (location && location.locationName) ||
        (location && location.boxId) ||
        (location && location.id) ||
        'Location';
      const data = months.map((meta) => {
        const entry =
          monthLookup.get(String(meta.key)) || monthLookup.get(String(meta.label));
        // Use entry directly since it has the new flat structure
        const totals = normalizeMonthlyTotals(entry || {});
        const spend = totals.totalSpend;
        return {
          x: meta.label,
          y: Number(spend) || 0,
          meta: {
            locationName: name,
            totals,
          },
        };
      });
      const hasSpend = data.some((point) => Number(point.y) > 0);
      return hasSpend
        ? {
            name,
            data,
          }
        : null;
    })
    .filter(Boolean);

  return { categories, series };
}

function buildAccountOptions(accounts) {
  const base = ensureArray(accounts).map((account) => {
    const raw =
      (account && (account.accountId || account.id || account.name)) || 'account';
    const label =
      (account && account.name) ||
      (account && account.displayName) ||
      (account && account.accountName) ||
      raw;
  return {
      value: String(raw),
      label,
    };
  });
  return [{ value: 'all', label: 'All accounts' }, ...base];
}

function buildLocationOptions(payload, accountId) {
  let locations = ensureArray(payload && payload.uiLocations);
  if (!locations.length) {
    locations = ensureArray(payload && payload.locations);
  }
  if (!locations.length) {
    locations = ensureArray(
      payload && payload.locationTraffic && payload.locationTraffic.locations
    );
  }
  if (accountId && accountId !== 'all') {
    locations = locations.filter(
      (loc) => String(loc && loc.accountId) === String(accountId)
    );
  }
  const base = locations.map((location) => {
    const raw =
      (location && (location.boxId || location.id || location.boxName)) ||
      'location';
    return {
      value: String(raw),
      label: formatLocationLabel(location),
    };
  });
  return [{ value: 'all', label: 'All locations' }, ...base];
}

function buildCombinedCategories(payload) {
  if (!payload) return [];
  const entries = Array.isArray(payload.combined) ? payload.combined : [];
  return entries
    .map((entry) => {
      const key = entry.month || entry.label;
      const label = entry.label || formatMonth(entry.month, entry.label);
      return key && label
        ? {
            key: String(key),
            label,
          }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => (a.key || '').localeCompare(b.key || ''));
}

function buildCombinedSeries(payload, meta) {
  const entries = Array.isArray(payload && payload.combined)
    ? payload.combined
    : [];
  if (!entries.length || !meta.length) {
    return [];
  }
  const monthLookup = new Map();
  entries.forEach((entry) => {
    const key = entry.month || entry.label;
    if (key) {
      monthLookup.set(String(key), entry);
    }
    if (entry.label) {
      monthLookup.set(String(entry.label), entry);
    }
  });
  const data = meta.map((item) => {
    const entry =
      monthLookup.get(String(item.key)) || monthLookup.get(String(item.label));
    // Use entry directly since it has the new flat structure
    const totals = normalizeMonthlyTotals(entry || {});
    const spend = totals.totalSpend;
    return {
      x: item.label,
      y: Number(spend) || 0,
      meta: {
        locationName: 'All locations',
        totals,
      },
    };
  });
  const hasSpend = data.some((point) => Number(point.y) > 0);
  return hasSpend
    ? [
        {
          name: 'All locations',
          data,
        },
      ]
    : [];
}

function normalizeMonthlyTotals(source = {}) {
  return parseBillingTotals({
    totalSpend: source.totalSpend,
    sms: source.sms,
    mms: source.mms,
    voice: source.voice || {
      count: 0,
      minutes: source.totalMinutes || 0,
      spend: 0,
      inbound: {
        count: 0,
        minutes: source.inboundMinutes || 0,
        spend: 0,
      },
      outbound: {
        count: 0,
        minutes: source.outboundMinutes || 0,
        spend: 0,
      },
    },
    video: source.video || {
      rooms: 0,
      participantMinutes: source.videoParticipantMinutes || 0,
      spend: 0,
      inbound: { rooms: 0, participantMinutes: 0, spend: 0 },
      outbound: {
        rooms: 0,
        participantMinutes: source.videoParticipantMinutes || 0,
        spend: 0,
      },
    },
    carrierCharges: source.carrierCharges,
    a2pRegistrationCharges: source.a2pRegistrationCharges,
  });
}

function ensureArray(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function formatCurrency(value, currency = 'USD') {
  const num = Number(value || 0);
  // Show exact decimal places from API response (up to 10 decimal places)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 10,
    useGrouping: true,
  }).format(num);
}

function formatMonth(month, fallback) {
  if (fallback) {
    return fallback;
  }
  if (!month) {
    return 'Unknown';
  }
  const parsed = moment(month, ['YYYY-MM', moment.ISO_8601], true);
  if (parsed.isValid()) {
    return parsed.format('MMMM YYYY');
  }
  const fallbackMoment = moment(month);
  return fallbackMoment.isValid() ? fallbackMoment.format('MMMM YYYY') : month;
}

function formatFootnote(range, currency, monthsCount) {
  const lines = [];
  lines.push(`${monthsCount} month${monthsCount === 1 ? '' : 's'} shown`);
  if (range && range.startDate && range.endDate) {
    const start = moment(range.startDate).format('MMM D, YYYY');
    const end = moment(range.endDate).format('MMM D, YYYY');
    lines.push(`Range: ${start} – ${end}`);
  }
  if (range && range.timezone) {
    lines.push(range.timezone);
  }
  if (currency) {
    lines.push(`Currency: ${currency.toUpperCase()}`);
  }
  return lines.join(' • ');
}

function buildTooltipAll(seriesConfig, categories, currency, index) {
  const label = categories[index] || '';
  if (!seriesConfig || !Array.isArray(seriesConfig)) {
    return '';
  }
  
  // Aggregate data across all locations
  let totalSpend = 0;
  let smsInCount = 0;
  let smsInSeg = 0;
  let smsInSpend = 0;
  let smsOutCount = 0;
  let smsOutSeg = 0;
  let smsOutSpend = 0;
  let mmsInCount = 0;
  let mmsInSeg = 0;
  let mmsInSpend = 0;
  let mmsOutCount = 0;
  let mmsOutSeg = 0;
  let mmsOutSpend = 0;
  let voiceSpend = 0;
  let voiceMin = 0;
  let videoSpend = 0;
  let videoMin = 0;
  
  seriesConfig.forEach((entry) => {
    const dataPoints = entry && entry.data;
    const point = dataPoints && dataPoints[index];
    if (!point || Number(point.y) === 0) {
      return;
    }
    const totals = parseBillingTotals((point.meta && point.meta.totals) || {});
    const sms = totals.sms || {};
    const mms = totals.mms || {};
    const voice = totals.voice || {};
    const video = totals.video || {};
    
    totalSpend += Number(totals.totalSpend || point.y || 0);
    smsInCount += Number((sms.inbound && sms.inbound.count) || 0);
    smsInSeg += Number((sms.inbound && sms.inbound.segments) || 0);
    smsInSpend += Number((sms.inbound && sms.inbound.spend) || 0);
    smsOutCount += Number((sms.outbound && sms.outbound.count) || 0);
    smsOutSeg += Number((sms.outbound && sms.outbound.segments) || 0);
    smsOutSpend += Number((sms.outbound && sms.outbound.spend) || 0);
    mmsInCount += Number((mms.inbound && mms.inbound.count) || 0);
    mmsInSeg += Number((mms.inbound && mms.inbound.segments) || 0);
    mmsInSpend += Number((mms.inbound && mms.inbound.spend) || 0);
    mmsOutCount += Number((mms.outbound && mms.outbound.count) || 0);
    mmsOutSeg += Number((mms.outbound && mms.outbound.segments) || 0);
    mmsOutSpend += Number((mms.outbound && mms.outbound.spend) || 0);
    voiceSpend += Number(voice.spend || 0);
    voiceMin += Number(voice.minutes || 0);
    videoSpend += Number(video.spend || 0);
    videoMin += Number(video.participantMinutes || 0);
  });
  
  if (totalSpend === 0 && smsInSeg === 0 && smsOutSeg === 0 && mmsInSeg === 0 && mmsOutSeg === 0) {
    return `<div style="padding:16px;background:linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.12);border:1px solid rgba(226,232,240,0.8);">
      <div style="font-weight:700;font-size:14px;color:#1e293b;margin-bottom:8px;letter-spacing:0.3px;">${label}</div>
      <div style="font-size:12px;color:#94a3b8;padding:8px 12px;background:#f1f5f9;border-radius:6px;text-align:center;">No spend recorded</div>
    </div>`;
  }
  
  return `<div style="padding:0;background:#ffffff;border-radius:12px;box-shadow:0 8px 32px rgba(15,23,42,0.15), 0 2px 8px rgba(15,23,42,0.08);border:1px solid rgba(226,232,240,0.8);overflow:hidden;min-width:280px;">
    <div style="background:linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);padding:14px 16px;color:#ffffff;">
      <div style="font-weight:700;font-size:13px;letter-spacing:0.5px;text-transform:uppercase;opacity:0.9;margin-bottom:4px;">${label}</div>
      <div style="font-weight:700;font-size:20px;display:flex;align-items:center;gap:8px;">
        <span style="font-size:14px;opacity:0.9;">All Locations:</span>
        <span style="font-size:20px;">${formatCurrency(totalSpend, currency)}</span>
      </div>
    </div>
    <div style="padding:16px;background:#f8fafc;">
      <div style="background:#ffffff;border-radius:10px;padding:12px;margin-bottom:12px;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <div style="width:4px;height:20px;background:linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%);border-radius:2px;"></div>
          <div style="font-weight:700;font-size:13px;color:#0f172a;letter-spacing:0.3px;">SMS</div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div style="background:#f0f9ff;border-radius:8px;padding:10px;border-left:3px solid #0ea5e9;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Inbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${smsInCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${smsInSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(smsInSpend, currency)}</span></div>
          </div>
          <div style="background:#fef3c7;border-radius:8px;padding:10px;border-left:3px solid #f59e0b;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Outbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${smsOutCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${smsOutSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(smsOutSpend, currency)}</span></div>
          </div>
        </div>
      </div>
      <div style="background:#ffffff;border-radius:10px;padding:12px;margin-bottom:12px;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,0.05);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <div style="width:4px;height:20px;background:linear-gradient(180deg, #ec4899 0%, #db2777 100%);border-radius:2px;"></div>
          <div style="font-weight:700;font-size:13px;color:#0f172a;letter-spacing:0.3px;">MMS</div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div style="background:#fdf2f8;border-radius:8px;padding:10px;border-left:3px solid #ec4899;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Inbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${mmsInCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${mmsInSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(mmsInSpend, currency)}</span></div>
          </div>
          <div style="background:#fce7f3;border-radius:8px;padding:10px;border-left:3px solid #f472b6;">
            <div style="font-size:11px;color:#64748b;text-transform:uppercase;font-weight:600;letter-spacing:0.5px;margin-bottom:6px;">Outbound</div>
            <div style="font-size:13px;color:#0f172a;font-weight:600;margin-bottom:2px;">${mmsOutCount}</div>
            <div style="font-size:11px;color:#64748b;margin-bottom:4px;">Segments: <span style="font-weight:600;color:#0f172a;">${mmsOutSeg}</span></div>
            <div style="font-size:11px;color:#64748b;">Spend: <span style="font-weight:600;color:#0f172a;">${formatCurrency(mmsOutSpend, currency)}</span></div>
          </div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div style="background:#ffffff;border-radius:10px;padding:12px;border:1px solid #e2e8f0;">
          <div style="font-weight:700;font-size:13px;color:#0f172a;margin-bottom:8px;">Voice</div>
          <div style="font-size:12px;color:#64748b;">Spend: <strong style="color:#0f172a;">${formatCurrency(voiceSpend, currency)}</strong></div>
          <div style="font-size:12px;color:#64748b;">Minutes: <strong style="color:#0f172a;">${voiceMin}</strong></div>
        </div>
        <div style="background:#ffffff;border-radius:10px;padding:12px;border:1px solid #e2e8f0;">
          <div style="font-weight:700;font-size:13px;color:#0f172a;margin-bottom:8px;">Video</div>
          <div style="font-size:12px;color:#64748b;">Spend: <strong style="color:#0f172a;">${formatCurrency(videoSpend, currency)}</strong></div>
          <div style="font-size:12px;color:#64748b;">Participant min: <strong style="color:#0f172a;">${videoMin}</strong></div>
        </div>
      </div>
    </div>
  </div>`;
}

export default withStyles(styles)(MessagingBilling);






// WEBPACK FOOTER //
// ./src/components/billing/MessagingBilling.js